import { UserIcon } from '@heroicons/react/24/outline';
import { isEmpty, unique } from 'radash';
import { useEffect, useRef, useState } from 'react';

import { Icon } from '@/components/core/Icon';
import useRealTimeMessaging from '@/hooks/useRealtimeMessaging';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';
import { useUser } from '@/hooks/useUser';
import { getInjector, getRootScope } from '@/services/angularjs-bridge';
import { logger } from '@/services/logger';
import { RealTimeMessagingActions, RealTimeMessagingEvents } from '@/types/RealTimeMessagingEvents';

const ConnectedUsers = ({ primaryKey }: { primaryKey: string }) => {

    // FIXME: Uncouple from the AngularJS implementation
    const $angularJsInjector = getInjector();
    const $rootScope = getRootScope($angularJsInjector);

    const { t } = useTranslation();
    const { toast } = useToast();
    const { user } = useUser();
    const [connectedUsersIds, setConnectedUsersIds] = useState<number[]>([]);
    const [connectedUsersNames, setConnectedUsersNames] = useState<string[]>([]);

    // Channel registration is owned by useItemEditedChannel in ItemEditorDialog.
    // This component only loads the current presence list and reacts to join/leave.
    const { getSubscribedUsers } = useRealTimeMessaging({
        eventName: RealTimeMessagingEvents.ITEM_EDITED_EVENT,
        identifier: primaryKey,
        eventHandler: (data) => {

            // If something is going on with the real time messages (ex. reconnecting) and
            // it's us who is register/unregister'ing, we should ignore the message.
            if (data.userId === user?.id) {
                return;
            }

            if (data.action === RealTimeMessagingActions.REGISTERED) {

                setConnectedUsersIds((prev) => {
                    if (prev.includes(data.userId)) {
                        // Already tracked (e.g. connection reset) — skip toast.
                        return prev;
                    }

                    const userName = $rootScope.getUserName(data.userId);
                    toast.info(t('USER_CONNECTED_TO_ITEM', {
                        user: userName
                    }));
                    return [...prev, data.userId];
                });

            } else if (data.action === RealTimeMessagingActions.UNREGISTERED) {

                setConnectedUsersIds((prev) => {
                    if (!prev.includes(data.userId)) {
                        return prev;
                    }

                    const userName = $rootScope.getUserName(data.userId);
                    toast.info(t('USER_DISCONNECTED_FROM_ITEM', {
                        user: userName
                    }));
                    return prev.filter(id => id != data.userId);
                });
            }

        }
    });

    const getSubscribedUsersRef = useRef(getSubscribedUsers);
    getSubscribedUsersRef.current = getSubscribedUsers;
    const userId = user?.id;

    useEffect(() => {

        // No need to track connected users if there is no primary key, i.e. new item.
        if (!primaryKey || isEmpty(primaryKey)) {
            return;
        }

        let cancelled = false;
        (async function() {
            // We are interested in all of the users that are changing the same item, except ourselves.
            const connectedUserIs = (await getSubscribedUsersRef.current()).filter(id => id !== userId);
            if (!cancelled) {
                setConnectedUsersIds(connectedUserIs);
            }
        })();

        return () => {
            cancelled = true;
        };

    }, [primaryKey, userId]);

    useEffect(() => {

        // Prepare user names from the connected ids
        if (isEmpty(connectedUsersIds)) {
            setConnectedUsersNames([]);
            return;
        }

        // Ensure that we have a unique list of users
        const uniqueConnectedUsersIds = unique(connectedUsersIds);

        const userNames = uniqueConnectedUsersIds.map(userId => {
            const userName = $rootScope.getUserName(userId);

            if (isEmpty(userName) || userId == userName) {
                logger.warn(`ConnectedUsers: Could not get user name with id ${userId}`);
            } else {
                return userName;
            }
        });

        setConnectedUsersNames(userNames);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [connectedUsersIds]);

    if (isEmpty(connectedUsersNames)) {
        return null;
    } else {
        return (
            <div className="tw-mr-auto">
                <Icon icon={UserIcon} size="sm" variant="dark" />
                <span className="tw-ml-1">
                    {connectedUsersNames.join(', ')}
                </span>
            </div>
        );
    }

};

export { ConnectedUsers };
